import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, MapPin, Star, Award, ArrowRight, CheckCircle2, Phone, Hammer, Home as HomeIcon } from "lucide-react";
import ApexLoader from "@/components/ApexLoader";
import api, { formatApiError } from "@/lib/api";

const DAMAGE_OPTIONS = [
  "Storm / wind damage",
  "Roof / hail damage",
  "Flood / water damage",
  "Fire / smoke damage",
  "Tree impact",
  "Mold remediation",
  "Other",
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

function ContractorCard({ c, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(c)}
      className={`text-left border ${
        selected ? "border-[#dc2626] bg-[#160c0c]" : "border-white/10 bg-[#0a0a0b] hover:border-white/25"
      } p-5 transition-all`}
      data-testid={`contractor-card-${c.contractor_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-display text-xl text-white tracking-tight leading-tight">{c.name}</div>
          <div className="font-mono-apex text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1 flex items-center gap-1.5">
            <MapPin size={10} /> {c.city}, {c.state}
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1 font-mono-apex text-[11px] text-white">
            <Star size={11} className="fill-[#dc2626] text-[#dc2626]" /> {c.rating}
          </div>
          <div className="font-mono-apex text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
            {c.jobs_completed.toLocaleString()} jobs
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {c.specialties.slice(0, 3).map((s) => (
          <span key={s} className="px-2 py-0.5 border border-white/10 font-mono-apex text-[9px] uppercase tracking-[0.15em] text-zinc-300">
            {s}
          </span>
        ))}
      </div>
      <div className="flex items-center gap-3 font-mono-apex text-[9px] uppercase tracking-[0.15em] text-zinc-500">
        <span className="flex items-center gap-1.5"><Award size={10} className="text-[#dc2626]" /> {c.certifications.join(" · ")}</span>
        <span className="text-zinc-700">·</span>
        <span>{c.years_active} yrs</span>
      </div>
      {selected && (
        <div className="mt-3 pt-3 border-t border-[#dc2626]/30 flex items-center gap-2 font-mono-apex text-[10px] uppercase tracking-[0.2em] text-[#dc2626]">
          <CheckCircle2 size={12} /> selected
        </div>
      )}
    </button>
  );
}

export default function FindAPro() {
  const [step, setStep] = useState(1); // 1=zip, 2=match, 3=contact, 4=success
  const [zip, setZip] = useState("");
  const [damageType, setDamageType] = useState("");
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const [state, setStateObj] = useState({ loading: false, error: "" });
  const [result, setResult] = useState(null);

  const search = async (e) => {
    e.preventDefault();
    setStateObj({ loading: true, error: "" });
    try {
      const { data } = await api.post("/find-pro/match", { zip, damage_type: damageType });
      setMatches(data.matches || []);
      if ((data.matches || []).length > 0) setSelected(data.matches[0]);
      setStep(2);
      setStateObj({ loading: false, error: "" });
    } catch (err) {
      setStateObj({ loading: false, error: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setStateObj({ loading: true, error: "" });
    try {
      const { data } = await api.post("/find-pro/lead", {
        ...contact,
        zip,
        damage_type: damageType || "Storm / wind damage",
        contractor_id: selected?.contractor_id || null,
      });
      setResult(data);
      setStep(4);
      setStateObj({ loading: false, error: "" });
    } catch (err) {
      setStateObj({ loading: false, error: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  const reset = () => {
    setStep(1);
    setZip("");
    setDamageType("");
    setMatches([]);
    setSelected(null);
    setContact({ name: "", email: "", phone: "", message: "" });
    setResult(null);
    setStateObj({ loading: false, error: "" });
  };

  return (
    <section
      id="find-a-pro"
      className="relative py-24 md:py-32 border-y border-white/5"
      style={{
        background:
          "radial-gradient(800px 400px at 80% 0%, rgba(220,38,38,0.08), transparent 60%), #0a0a0b",
      }}
      data-testid="find-a-pro-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-[420px_1fr] gap-10 items-start">
          {/* Left: pitch */}
          <div className="lg:sticky lg:top-32">
            <div className="flex items-center gap-3 mb-6">
              <span className="block w-8 h-px bg-[#dc2626]" />
              <span className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                for homeowners · always free
              </span>
            </div>
            <h2 className="font-display text-5xl md:text-6xl text-white tracking-tight leading-[0.95] mb-6">
              <span className="silver-text italic">Help</span> is one zip code away.
            </h2>
            <p className="font-body text-base text-zinc-400 leading-relaxed mb-8">
              Storm just hit your home? Tell us the damage and your zip — we'll match you with a
              vetted, certified restoration company in your area. They'll reach out within 30
              minutes.
            </p>
            <ul className="space-y-3 font-mono-apex text-xs text-zinc-300">
              {[
                "IICRC-certified contractors only",
                "Background-checked & insured",
                "Real customer ratings & job history",
                "Zero cost to you · always",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-[#dc2626] mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: stepper card */}
          <div className="border border-white/10 bg-[#121214] p-8 md:p-10 min-h-[480px]" data-testid="find-a-pro-card">
            <div className="flex items-center gap-2 mb-8 font-mono-apex text-[10px] uppercase tracking-[0.25em]">
              {[
                { n: 1, l: "ZIP" },
                { n: 2, l: "Match" },
                { n: 3, l: "Contact" },
                { n: 4, l: "Done" },
              ].map((s, idx, arr) => (
                <React.Fragment key={s.n}>
                  <span
                    className={`flex items-center gap-1.5 ${
                      step >= s.n ? "text-[#dc2626]" : "text-zinc-700"
                    }`}
                  >
                    <span
                      className={`w-5 h-5 flex items-center justify-center border ${
                        step >= s.n ? "border-[#dc2626]" : "border-zinc-800"
                      }`}
                    >
                      {step > s.n ? <CheckCircle2 size={10} /> : s.n}
                    </span>
                    <span>{s.l}</span>
                  </span>
                  {idx < arr.length - 1 && <span className="flex-1 h-px bg-white/5" />}
                </React.Fragment>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {/* STEP 1 — ZIP */}
              {step === 1 && (
                <motion.form
                  key="step1"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  onSubmit={search}
                  className="space-y-5"
                  data-testid="findpro-step1"
                >
                  <div>
                    <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                      Your home's ZIP code
                    </label>
                    <div className="relative">
                      <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                      <input
                        required
                        value={zip}
                        onChange={(e) => setZip(e.target.value)}
                        placeholder="e.g. 75201"
                        inputMode="numeric"
                        className="input-apex w-full pl-11 pr-4 py-4 font-mono-apex text-lg tracking-[0.2em] text-white"
                        data-testid="findpro-zip-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                      Type of damage (optional)
                    </label>
                    <select
                      value={damageType}
                      onChange={(e) => setDamageType(e.target.value)}
                      className="input-apex w-full px-4 py-3 font-body text-sm appearance-none cursor-pointer"
                      data-testid="findpro-damage-input"
                    >
                      <option value="" className="bg-[#0a0a0b]">Not sure yet</option>
                      {DAMAGE_OPTIONS.map((d) => (
                        <option key={d} value={d} className="bg-[#0a0a0b]">{d}</option>
                      ))}
                    </select>
                  </div>
                  {state.error && <div className="font-mono-apex text-xs text-[#dc2626]">{state.error}</div>}
                  <button
                    type="submit"
                    disabled={state.loading || !zip.trim()}
                    className="w-full btn-primary-apex px-6 py-4 font-mono-apex text-[11px] uppercase tracking-[0.25em] disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="findpro-search-btn"
                  >
                    {state.loading ? (<><ApexLoader inline size={14} /> Searching…</>) : (<>Find my pro <ArrowRight size={14} /></>)}
                  </button>
                  <p className="font-mono-apex text-[10px] text-zinc-600 leading-relaxed">
                    By searching you agree to receive contact from a certified Apex restoration
                    partner. No spam. No obligation.
                  </p>
                </motion.form>
              )}

              {/* STEP 2 — Match */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  className="space-y-5"
                  data-testid="findpro-step2"
                >
                  <div>
                    <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                      {matches.length > 0 ? `${matches.length} matches near ${zip}` : `building network in ${zip}`}
                    </div>
                    <h3 className="font-display text-2xl text-white">
                      {matches.length > 0
                        ? "Pick the best fit · or we'll route automatically."
                        : "Verified pros coming soon · we'll route you the moment one joins."}
                    </h3>
                  </div>
                  <div className="grid gap-3 max-h-[360px] overflow-y-auto scroll-thin pr-1">
                    {matches.map((m, i) => (
                      <motion.div key={m.contractor_id} variants={fadeUp} custom={i} initial="hidden" animate="show">
                        <ContractorCard
                          c={m}
                          selected={selected?.contractor_id === m.contractor_id}
                          onSelect={setSelected}
                        />
                      </motion.div>
                    ))}
                    {matches.length === 0 && (
                      <>
                        {[0, 1, 2].map((i) => (
                          <motion.div
                            key={`placeholder-${i}`}
                            variants={fadeUp}
                            custom={i}
                            initial="hidden"
                            animate="show"
                            className="p-4 border border-white/10 bg-[#121214] flex items-center gap-4"
                            data-testid="findpro-placeholder-card"
                          >
                            <div className="w-12 h-12 border border-white/10 bg-[#0a0a0b] flex items-center justify-center shrink-0">
                              <Hammer size={18} className="text-zinc-600" strokeWidth={1.5} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-display text-lg text-zinc-300 tracking-tight">Verified pro coming soon</div>
                              <div className="font-mono-apex text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                                vetting in progress · {zip} service area
                              </div>
                            </div>
                            <div className="px-2 py-1 border border-[#dc2626]/40 bg-[#dc2626]/10 font-mono-apex text-[9px] uppercase tracking-[0.2em] text-[#fca5a5] shrink-0">
                              Reserved
                            </div>
                          </motion.div>
                        ))}
                        <div className="mt-2 p-3 border border-white/5 bg-[#0a0a0b] font-mono-apex text-[10px] uppercase tracking-[0.2em] text-zinc-400 text-center">
                          Continue below — Apex will route your job to our highest-rated regional partner.
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setStep(1)}
                      className="btn-secondary-apex px-5 py-3 font-mono-apex text-[10px] uppercase tracking-[0.25em]"
                      data-testid="findpro-back-btn"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => setStep(3)}
                      className="flex-1 btn-primary-apex px-5 py-3 font-mono-apex text-[10px] uppercase tracking-[0.25em] flex items-center justify-center gap-2"
                      data-testid="findpro-continue-btn"
                    >
                      Get connected <ArrowRight size={14} />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* STEP 3 — Contact */}
              {step === 3 && (
                <motion.form
                  key="step3"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  onSubmit={submit}
                  className="space-y-4"
                  data-testid="findpro-step3"
                >
                  <div>
                    <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">routing to</div>
                    <h3 className="font-display text-2xl text-white">{selected?.name || "Best regional match"}</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-1.5">First / last name</label>
                      <input
                        required
                        value={contact.name}
                        onChange={(e) => setContact({ ...contact, name: e.target.value })}
                        className="input-apex w-full px-3 py-2.5 font-body text-sm"
                        data-testid="findpro-name-input"
                      />
                    </div>
                    <div>
                      <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-1.5">Phone</label>
                      <input
                        required
                        type="tel"
                        value={contact.phone}
                        onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                        className="input-apex w-full px-3 py-2.5 font-body text-sm"
                        data-testid="findpro-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-1.5">Email</label>
                    <input
                      required
                      type="email"
                      value={contact.email}
                      onChange={(e) => setContact({ ...contact, email: e.target.value })}
                      className="input-apex w-full px-3 py-2.5 font-body text-sm"
                      data-testid="findpro-email-input"
                    />
                  </div>
                  <div>
                    <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-1.5">Brief description (optional)</label>
                    <textarea
                      rows={3}
                      value={contact.message}
                      onChange={(e) => setContact({ ...contact, message: e.target.value })}
                      placeholder="e.g. Hailstorm last night — visible roof shingle damage"
                      className="input-apex w-full px-3 py-2.5 font-body text-sm resize-none"
                      data-testid="findpro-message-input"
                    />
                  </div>
                  {state.error && <div className="font-mono-apex text-xs text-[#dc2626]">{state.error}</div>}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="btn-secondary-apex px-5 py-3 font-mono-apex text-[10px] uppercase tracking-[0.25em]"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={state.loading}
                      className="flex-1 btn-primary-apex px-5 py-3 font-mono-apex text-[10px] uppercase tracking-[0.25em] flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="findpro-submit-btn"
                    >
                      {state.loading ? (<><ApexLoader inline size={14} /> Sending…</>) : (<>Send my request <ArrowRight size={14} /></>)}
                    </button>
                  </div>
                </motion.form>
              )}

              {/* STEP 4 — Success */}
              {step === 4 && result && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  className="text-center py-8"
                  data-testid="findpro-step4"
                >
                  <div className="w-14 h-14 mx-auto mb-6 border border-[#dc2626] bg-[#dc2626]/10 flex items-center justify-center">
                    <CheckCircle2 size={26} className="text-[#dc2626]" />
                  </div>
                  <h3 className="font-display text-3xl text-white mb-3">Help is on the way.</h3>
                  <p className="font-body text-sm text-zinc-300 leading-relaxed mb-6 max-w-md mx-auto">
                    We've routed your request to{" "}
                    <strong className="text-white">{result.contractor?.name}</strong> in{" "}
                    {result.contractor?.city}, {result.contractor?.state}. Expect a call or text
                    within <span className="text-[#dc2626]">30 minutes</span>.
                  </p>
                  <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-6">
                    confirmation id · {result.id?.slice(0, 8)}
                  </div>
                  <button
                    onClick={reset}
                    className="btn-secondary-apex px-6 py-3 font-mono-apex text-[10px] uppercase tracking-[0.25em]"
                    data-testid="findpro-reset-btn"
                  >
                    Submit another request
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
